"use client";

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  Row,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import {Loader2} from "lucide-react";

import {
  TablePagination,
  PAGE_SIZE_OPTIONS,
} from "@/components/ui/TablePagination";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {cn} from "@/lib/utils";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  pagination?: PaginationState;
  onPaginationChange?: (state: PaginationState) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (state: RowSelectionState) => void;
  getRowId: (row: TData) => string;
  selectedRow?: TData;
  onSelectRow?: (value: TData) => void;
  withoutHeader?: boolean;
  totalCount: number;
  forceSelectRow?: boolean;
  selectedPrimaryStyles?: boolean;
  hideRowsPerPage?: boolean;
  enableRowSelection?: (row: Row<TData>) => boolean;
  multiSelect?: boolean;
  tableHeadClassName?: string;
  tableCellClassName?: string;
  wrapperClassName?: string;
  manualPagination?: boolean;
  isLoading?: boolean;
  onRowClick?: (row: TData) => void;
}

const DataTable = <TData, TValue>({
  columns,
  data,
  pagination,
  onPaginationChange,
  rowSelection,
  onRowSelectionChange,
  selectedRow,
  onSelectRow,
  getRowId,
  withoutHeader,
  totalCount,
  forceSelectRow,
  selectedPrimaryStyles,
  enableRowSelection,
  multiSelect = false,
  tableHeadClassName,
  tableCellClassName,
  wrapperClassName,
  manualPagination = true,
  isLoading = false,
  hideRowsPerPage = false,
  onRowClick,
}: DataTableProps<TData, TValue>) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange,
    getRowId,
    onRowSelectionChange,
    manualPagination: manualPagination,
    pageCount:
      totalCount > 0 ? Math.ceil(totalCount / (pagination?.pageSize || 1)) : 0,
    state: {
      pagination,
      rowSelection: rowSelection ?? {},
    },
    enableRowSelection: enableRowSelection,
  });

  return (
    <div
      className={cn(
        "rounded-md border overflow-hidden relative",
        wrapperClassName,
      )}
    >
      {isLoading && (
        <div className="flex justify-center items-center h-full absolute top-0 left-0 w-full bg-background/50 z-10">
          <Loader2 className="w-10 h-10 text-secondary animate-spin" />
        </div>
      )}
      <Table>
        {!withoutHeader && (
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <TableHead
                      className={cn("whitespace-nowrap", tableHeadClassName)}
                      key={header.id}
                      style={{
                        width: header.column.getSize()
                          ? `${header.column.getSize()}px`
                          : "auto",
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}

        <TableBody className="bg-background">
          {table.getRowModel().rows.map(row => (
            <TableRow
              key={row.id}
              data-state={
                row.getIsSelected() ||
                (selectedRow && getRowId(selectedRow) === row.id && "selected")
              }
              className={cn(
                "group hover:bg-action-hover-bg cursor-pointer focus:bg-muted",
                {
                  "opacity-50 cursor-not-allowed": !row.getCanSelect(),
                },
              )}
              onClick={() => {
                if (onRowClick) {
                  onRowClick(row.original);
                } else if (
                  (!rowSelection || forceSelectRow) &&
                  row.getCanSelect()
                ) {
                  onSelectRow?.(row.original);
                }
              }}
            >
              {row.getVisibleCells().map(cell => {
                return (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      {
                        "bg-primary-background !text-primary-dark light:[&>*]:!text-primary-dark light:[&>*>*]:!text-primary-dark light:[&>*>*>*]:!text-primary-dark light:[&>*>*>svg]:!text-white":
                          (row.getIsSelected() ||
                            (selectedRow && getRowId(selectedRow) === row.id) ||
                            (multiSelect && row.getIsSelected())) &&
                          selectedPrimaryStyles,
                      },
                      tableCellClassName,
                    )}
                    style={{
                      textAlign:
                        cell.column.id === "actions" ? "right" : "left",
                      width: cell.column.getSize()
                        ? `${cell.column.getSize()}px`
                        : "auto",
                    }}
                    onClick={e => {
                      if (cell.column.id === "select") {
                        e.stopPropagation();
                      }
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pagination && (
        <TablePagination
          totalCount={totalCount || data.length}
          pageCount={table.getPageCount()}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canPreviousPage={table.getCanPreviousPage()}
          canNextPage={table.getCanNextPage()}
          onFirstPage={() => table.setPageIndex(0)}
          onPreviousPage={() => table.previousPage()}
          onNextPage={() => table.nextPage()}
          onLastPage={() => table.setPageIndex(table.getPageCount() - 1)}
          onPageSizeChange={table.setPageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          hideRowsPerPage={hideRowsPerPage}
        />
      )}
    </div>
  );
};

export default DataTable;
